import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    Self,
    ViewChild,
    OnInit,
    HostBinding,
} from '@angular/core';
import { InfiniteListComponent } from '@bazis/shared/components/infinite-list.component';
import { ControlValueAccessor, NgControl, FormControl } from '@angular/forms';
import { EntData, SearchSettings } from '@bazis/shared/models/srv.types';
import { TooltipSettings } from '@bazis/form/models/form-element.types';
import { combineLatest, Observable } from 'rxjs';
import { BazisEntityService } from '@bazis/shared/services/entity.service';
import { map } from 'rxjs/operators';
import { BazisConfigurationService } from '@bazis/configuration.service';
import { ButtonElement } from '@bazis/shared/components/web/button/button.component';

// Всплывашка вверх, по кнопке, внутри строка с поиском по списку bazis-input-options
@Component({
    selector: 'bazis-button-search',
    templateUrl: './button-search.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class BazisButtonSearchComponent
    extends InfiniteListComponent
    implements ControlValueAccessor, OnInit
{
    @HostBinding('class') get hostStyle() {
        return {
            'bazis-list': false,
            'bazis-list_control-dd': false,
        };
    }

    @Input() entityType: string = null;

    @Input() buttonOpenKey: string = null;

    @Input() buttonApplyKey: string = null;

    @Input() searchSettings: SearchSettings = null;

    @Output() touched = new EventEmitter();

    // знак вопроса может быть рядом с полем
    @Input() tooltipKey: string = null;

    // заголовок поля
    @Input() titleKey: string = null;

    // возможно, нужен будет какой-то коммент под полем
    @Input() noteKey: string = null;

    // параметры для tooltip
    @Input() tooltipParams: any = null;

    // параметры для title
    @Input() titleParams: any = null;

    // параметры для note
    @Input() noteParams: any = null;

    // настройки отображения и работы тултипа
    @Input() tooltipSettings: TooltipSettings = null;

    @Input() excludeIds$: Observable<string[]> = null;

    @Input() staticOptions$: Observable<EntData[]> = null;

    @Input() selectAllTreshhold: number = 20;

    @Input() modifyItemFn: Function;

    @Input() fillButton: string = 'outline';

    @Input() applyValuesOnHide = true;

    @Input() showApplyButton = true;

    // размер поля, если не задан, то используется дефолтный
    @Input() sizeControl: 'large' | 'compact' | 'mini' | '' = '';

    @ViewChild('toggleBtn') toggleBtn: ButtonElement;

    searchControl = new FormControl();

    selected = new FormControl([]);

    isSearchFocused = false;

    public onChange = (_: any) => {};

    public onTouched = () => {};

    isTouched = false;

    isDisabled = false;

    open = false;

    formattedList$;

    icons;

    constructor(
        @Self() public ngControl: NgControl,
        protected entityService: BazisEntityService,
        private configurationService: BazisConfigurationService,
    ) {
        super(entityService);
        ngControl.valueAccessor = this;
        this.icons = this.configurationService.controlIcons;
    }

    ngOnInit() {
        this.search$ = this.searchControl.valueChanges;

        super.ngOnInit();

        if (!this.showApplyButton) this.applyValuesOnHide = true;

        if (this.staticOptions$) {
            this.list$ = combineLatest([
                this.staticOptions$,
                this.searchUpdated$,
                this.needNextPage$,
            ]).pipe(
                map(([options, search]) => {
                    const str = search ? search.toLowerCase() : '';
                    return search
                        ? options.filter(
                              (option) =>
                                  Object.keys(option.$snapshot).findIndex(
                                      (property) =>
                                          property !== 'id' &&
                                          property !== 'type' &&
                                          typeof option.$snapshot[property] !== 'object' &&
                                          option.$snapshot[property]
                                              .toString()
                                              .toLowerCase()
                                              .indexOf(str) > -1,
                                  ) > -1,
                          )
                        : options;
                }),
            );
        }
        this.formattedList$ = this.list$.pipe(
            map((list) => {
                const listElements = this.modifyItemFn
                    ? list.map((item) => this.modifyItemFn(item))
                    : list;
                return [...listElements];
            }),
        );
    }

    nextPage() {
        if (this.searchSettings) {
            this.needNextPage$.next(true);
        }
    }

    writeValue() {}

    public registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
        if (isDisabled) this.searchControl.disable();
        else this.searchControl.enable();
    }

    public markAsTouched() {
        if (!this.isTouched) {
            this.onTouched();
            this.isTouched = true;
            this.touched.emit();
        }
    }

    togglePopup() {
        this.open = !this.open;
    }

    apply() {
        if (this.selected.value && this.selected.value.length > 0) {
            this.onChange([...this.selected.value]);
        }
        this.searchControl.setValue('');
        this.selected.setValue([]);

        this.togglePopup();
    }

    onClickOutside() {
        if (this.isSearchFocused) return;

        if (this.applyValuesOnHide) {
            this.apply();
        } else {
            this.open = false;
        }
    }
}
